import axios from 'axios';
import appAxios from '../axios';
import { PLATFORM_HEADER, PLATFORM_WEB } from '@nxsys/constants/session';

/**
 * Two-factor authentication (NX-4996).
 *
 * Every call takes an explicit `baseUrl`. Login posts to both backends at once
 * (see `dualLogin.ts`) and the challenge token is minted by exactly one of
 * them, so the follow-up calls have to go back to that same system — umbrella
 * and bureau are separate deployments over separate databases.
 */

export type TwoFactorMode = 'verify' | 'enrol';

export interface TwoFactorChallengeResponse {
  twoFactorRequired: true;
  mode: TwoFactorMode;
  challengeToken: string;
  /**
   * When the challenge token stops being accepted, ISO from the server.
   *
   * The binding clock on the code screen, and the shorter of the two running
   * there: an emailed code lives ten minutes but can only be spent through a
   * live challenge, so past this instant Verify, Resend code and Try another way
   * all fail together — every one of them resolves the challenge token first.
   *
   * Optional because a deployment on the previous backend does not send it. The
   * screen falls back to reacting to the failure rather than pre-empting it.
   */
  challengeExpiresAt?: string;
  success: false;
}

export interface TwoFactorSetupResponse {
  otpauthUrl: string;
  qrDataUrl: string;
  /** Grouped in fours for the "can't scan" panel. */
  secret: string;
  expiresAt: string;
}

export interface TwoFactorSessionResponse {
  token: string;
  exp: number;
  user: Record<string, any>;
  success: boolean;
  backupCodes?: string[];
}

export const isTwoFactorChallenge = (
  data: any,
): data is TwoFactorChallengeResponse => data?.twoFactorRequired === true;

/**
 * Every call here can be aimed at the *other* deployment, and one of them is
 * awaited before the user is let in — so a peer that hangs, as opposed to one that
 * refuses, would leave that await pending forever: signed in, navigated, no message,
 * and `setAlternateSystem` never firing (Victor, review of #1755). There is no
 * `axios.defaults.timeout` in this app, so without this there is no ceiling at all.
 *
 * Shorter than `dualLogin.ts`'s 15s deliberately. That one runs before anything has
 * rendered; this one can run with the user already authenticated and waiting to be
 * let in, where a long spinner reads as broken. Timing out is not a failure here —
 * the caller degrades to "the other Nxsys system needs a fresh code".
 */
const TIMEOUT = 10000;

const post = async <T>(
  baseUrl: string,
  path: string,
  body: any,
): Promise<T> => {
  const res = await axios.post(`${baseUrl}${path}`, body, {
    timeout: TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      // Bypasses ApiConfig, and `verify`/`confirm-setup` return a session token,
      // so the platform marker has to be set here too — otherwise a 2FA login
      // opts out of the idle timeout entirely (NX-5238; Victor, review of
      // #1753). Same failure `dualLogin` and `forgotPassword` already had.
      [PLATFORM_HEADER]: PLATFORM_WEB,
    },
  });
  return res.data as T;
};

export const startTwoFactorSetup = (baseUrl: string, challengeToken: string) =>
  post<TwoFactorSetupResponse>(baseUrl, '/auth/2fa/setup', { challengeToken });

export const confirmTwoFactorSetup = (
  baseUrl: string,
  challengeToken: string,
  code: string,
) =>
  post<TwoFactorSessionResponse>(baseUrl, '/auth/2fa/confirm-setup', {
    challengeToken,
    code,
  });

/**
 * Which factor the code came from.
 *
 * An emailed code is also six digits, so the backend cannot tell it from a TOTP
 * by looking — the client has to say. Omitted means the authenticator, which
 * covers a recovery code too: that path is recognised by shape.
 */
export type TwoFactorCodeMethod = 'app' | 'email';

export const verifyTwoFactorCode = (
  baseUrl: string,
  challengeToken: string,
  code: string,
  method?: TwoFactorCodeMethod,
) =>
  post<TwoFactorSessionResponse>(baseUrl, '/auth/2fa/verify', {
    challengeToken,
    code,
    method,
  });

export interface TwoFactorEmailCodeResponse {
  /** Masked, e.g. `t••••g@nxsys.tech` — the backend never echoes the address. */
  sentTo: string;
  expiresAt: string;
  /** Earliest the user may ask for another; the cooldown is enforced server-side. */
  resendAfter: string;
}

/** "Try another way" → email a one-time code to the address on the account. */
export const sendTwoFactorEmailCode = (
  baseUrl: string,
  challengeToken: string,
) =>
  post<TwoFactorEmailCodeResponse>(baseUrl, '/auth/2fa/email-code', {
    challengeToken,
  });

// --------------------------------------------------------------- settings --
//
// Everything below runs on an established session, so it goes through the app's
// configured axios instance (base URL + Bearer header) rather than taking a
// `baseUrl` — by this point the user is signed in to exactly one system.

export interface TwoFactorStatus {
  enabled: boolean;
  enforced: boolean;
  enabledAt: string | null;
  backupCodesRemaining: number;
  /**
   * Whether *this session* should be asked to set up 2FA (NX-5764).
   *
   * `enforced && !enabled` is the account-level answer, and it is what this
   * screen used to compute for itself. The server answers it now because two
   * kinds of session cannot honestly be asked and one of them is invisible from
   * here: a master-password login already skips the challenge at sign-in (D7),
   * but the login response deliberately strips the flag that says so, so no
   * amount of client-side checking can see it.
   *
   * Optional because an older backend does not send it; the caller falls back to
   * the account-level rule, which is what it did before.
   */
  mustEnrol?: boolean;
}

export const getTwoFactorStatus = async (): Promise<TwoFactorStatus> => {
  const res = await appAxios.get('/auth/2fa/status');
  return res.data;
};

/**
 * Confirms the password and mints a challenge — the same flow login uses.
 *
 * `enrol` (the default) opens the enrolment wizard. `rotate` is step one of moving
 * to a new device: the resulting token is what unlocks step two, and the two are
 * not interchangeable server-side.
 */
export const reauthForTwoFactor = async (
  password: string,
  mode?: 'enrol' | 'rotate',
): Promise<{ challengeToken: string }> => {
  const res = await appAxios.post('/auth/2fa/reauth', { password, mode });
  return res.data;
};

/**
 * New phone, step two. The old authenticator keeps working until confirmed.
 *
 * Carries the password as well as the step-one token: the token is what stops the
 * flow being entered, the password is what the change hangs on.
 */
export const startTwoFactorRotate = async (
  challengeToken: string,
  password: string,
  code: string,
  method?: TwoFactorCodeMethod,
): Promise<TwoFactorSetupResponse> => {
  const res = await appAxios.post('/auth/2fa/rotate', {
    challengeToken,
    password,
    code,
    method,
  });
  return res.data;
};

/**
 * "I no longer have my phone" — emails a code that authorises moving the
 * authenticator. For someone who has lost the device *and* their recovery codes;
 * anyone still holding a recovery code can just type it into the same field.
 */
export const sendTwoFactorRotateEmailCode = async (
  challengeToken: string,
): Promise<TwoFactorEmailCodeResponse> => {
  const res = await appAxios.post('/auth/2fa/rotate/email-code', {
    challengeToken,
  });
  return res.data;
};

/** New phone, step two. From here the old authenticator stops working. */
export const confirmTwoFactorRotate = async (
  code: string,
): Promise<TwoFactorSessionResponse> => {
  const res = await appAxios.post('/auth/2fa/rotate/confirm', { code });
  return res.data;
};

export const disableTwoFactor = async (
  password: string,
  code: string,
): Promise<TwoFactorSessionResponse> => {
  const res = await appAxios.post('/auth/2fa/disable', { password, code });
  return res.data;
};

/** Pulls the message the backend actually sent, not axios's generic wrapper. */
export const twoFactorErrorMessage = (error: any, fallback: string): string => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message[0] || fallback;
  return message || fallback;
};
